import React from 'react';
import { ImageCarousel } from '../../UI/image-carousel';
import { AuthenticatedImage } from '../../UI/authenticated-image';
import { IActivityDisplayState } from '../../../Models/trips/trip-activity.model';
import { ActivityImageSlideshow } from '../../activities/activity-image-slideshow';
import styles from './base-item-card.module.css';

interface ItemCardMediaProps {
  photos?: string[];
  thumbnailUrl?: string;
  placeName: string;
  layout?: 'compact' | 'standard' | 'detailed';
  displayState?: IActivityDisplayState;
  videoId?: string;
  autoPlayVideoToken?: string;
  mediaPlaybackSessionToken?: string;
  mediaPlaybackOwner?: boolean;
  onEditDisplayClick?: () => void;
}

const ItemCardMediaComponent: React.FC<ItemCardMediaProps> = ({
  photos,
  thumbnailUrl,
  placeName,
  layout = 'standard',
  displayState,
  videoId,
  autoPlayVideoToken,
  mediaPlaybackSessionToken,
  mediaPlaybackOwner,
  onEditDisplayClick
}) => {
  if (displayState) {
    return (
      <div className={styles.mediaWrapper}>
        <ActivityImageSlideshow
          displayState={displayState}
          videoId={videoId}
          autoPlayToken={autoPlayVideoToken}
          playbackSessionToken={mediaPlaybackSessionToken}
          playbackOwner={mediaPlaybackOwner}
          altText={placeName}
          fallbackImageUrl={thumbnailUrl}
          onEditClick={onEditDisplayClick}
        />
      </div>
    );
  }

  if (!photos || photos.length === 0) {
    if (thumbnailUrl) {
      return (
        <div className={styles.thumbnail}>
          <AuthenticatedImage
            src={thumbnailUrl}
            alt={placeName}
          />
        </div>
      );
    }
    return null;
  }

  return (
    <div className={styles.mediaWrapper}>
      <ImageCarousel
        photos={photos}
        placeName={placeName}
      />
    </div>
  );
};

// Memoize with custom comparison to prevent re-renders when photos array reference changes
export const ItemCardMedia = React.memo(
  ItemCardMediaComponent,
  (prevProps, nextProps) => {
    if (prevProps.thumbnailUrl !== nextProps.thumbnailUrl) return false;
    if (prevProps.placeName !== nextProps.placeName) return false;
    if (prevProps.layout !== nextProps.layout) return false;
    if (prevProps.videoId !== nextProps.videoId) return false;
    if (prevProps.autoPlayVideoToken !== nextProps.autoPlayVideoToken)
      return false;
    if (
      prevProps.mediaPlaybackSessionToken !==
      nextProps.mediaPlaybackSessionToken
    )
      return false;
    if (prevProps.mediaPlaybackOwner !== nextProps.mediaPlaybackOwner)
      return false;
    if (prevProps.displayState !== nextProps.displayState) return false;
    if (prevProps.onEditDisplayClick !== nextProps.onEditDisplayClick)
      return false;

    // Compare photos arrays
    if (prevProps.photos === nextProps.photos) return true;
    if (!prevProps.photos && !nextProps.photos) return true;
    if (!prevProps.photos || !nextProps.photos) return false;
    if (prevProps.photos.length !== nextProps.photos.length) return false;

    // Check if all photo URLs are the same
    for (let i = 0; i < prevProps.photos.length; i++) {
      if (prevProps.photos[i] !== nextProps.photos[i]) return false;
    }
    return true;
  }
);
